import _ from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useDataMap } from "../../common/hooks/api/useDataMap";
import { NetworkRequestState } from "../../common/models";
import { Api } from "../../common/service/Api";
import { useDataMapPendingSourceAssocationCollectionGroupIds } from "../../common/service/resources/data-map/dataMapPending";
import { CollectionGroupType } from "../../common/service/server/controller/CollectionGroupsController";
import { CollectionGroupDetailsDto } from "../../common/service/server/dto/CollectionGroupDto";
import { DataMapDto } from "../../common/service/server/dto/datamap/DataMapDto";
import {
  CollectionContext,
  OrganizationDataSourceDto,
} from "../../common/service/server/dto/OrganizationDataRecipientDto";
import { OrganizationPublicId, UUIDString } from "../../common/service/server/Types";
import { DataRecipientMultiselectProps } from "@truevault/aquilo";
import { toggleSelection } from "../surveys/util";
import { EXCLUDE_GROUP_EMPLOYMENT, useCollectionGroups } from "./useCollectionGroups";
import { useOrganizationVendors, useOrgDataSources } from "./useOrganizationVendors";

export type UseVendorAssociation = {
  selected: string[];
  group: CollectionGroupDetailsDto | undefined;
  fetched: {
    collectionGroups: CollectionGroupDetailsDto[];
    dataMap: DataMapDto;
    sources: OrganizationDataSourceDto[];
  };
  requests: {
    collectionGroups: NetworkRequestState;
    sources: NetworkRequestState;
    dataMap: NetworkRequestState;
  };
  multiselect: DataRecipientMultiselectProps;
  error?: boolean;
};

export const useDataSourceAssociation = (
  orgId: OrganizationPublicId,
  groupId: string,
  excludeGroups: CollectionGroupType[],
  collectionContext?: CollectionContext[],
): UseVendorAssociation => {
  const [selected, setSelected] = useState<string[]>([]);

  const [groups, groupsRequest] = useCollectionGroups(orgId, undefined, excludeGroups);
  const [sources, sourcesRequest] = useOrgDataSources(orgId);
  const { data: dataMap, request: dataMapRequest } = useDataMap(orgId);

  const relevantSources = useMemo(() => {
    if (!collectionContext) {
      return sources ?? [];
    }

    return (
      sources?.filter((s) => _.intersection(s.collectionContext, collectionContext).length > 0) ??
      []
    );
  }, [collectionContext, sources]);

  const onChange = ({ appId, included }) => {
    setSelected((prevSelected) => toggleSelection(prevSelected, appId, included));
    Api.dataMap.patchDataMap(orgId, {
      operations: [
        {
          target: "SOURCE_ASSOCIATION",
          action: included ? "ADD" : "REMOVE",
          groupId,
          vendorId: appId,
        },
      ],
    });
  };

  const onChangeSelectAll = (selected: boolean) => {
    relevantSources.forEach((s) => onChange({ appId: s.vendorId, included: selected }));
  };

  const group = useMemo(() => groups?.find((g) => g.id == groupId), [groups, groupId]);

  // deserialize remote state into local state
  useEffect(() => {
    if (dataMap && group) {
      const associatedVendorIds = dataMap.received
        .filter((d) => d.groupId == group.id)
        .map((d) => d.vendorId);
      setSelected(associatedVendorIds);
    }
  }, [dataMap, group]);

  return {
    selected,
    group,
    fetched: {
      sources: relevantSources,
      dataMap,
      collectionGroups: groups,
    },
    requests: {
      sources: sourcesRequest,
      dataMap: dataMapRequest,
      collectionGroups: groupsRequest,
    },
    multiselect: {
      recipients: relevantSources,
      selected,
      onChange,
      onChangeSelectAll,
    },
  };
};

export const useDataRecipientAssociation = (
  orgId: OrganizationPublicId,
  groupId: string,
  excludeGroups: CollectionGroupType[],
  collectionContext?: CollectionContext[],
): UseVendorAssociation => {
  const [selected, setSelected] = useState<string[]>([]);

  const [groups, groupsRequest] = useCollectionGroups(orgId, undefined, excludeGroups);
  const [recipients, recipientsRequest] = useOrganizationVendors(orgId);
  const { data: dataMap, request: dataMapRequest } = useDataMap(orgId);

  const relevantRecipients = useMemo(() => {
    if (!collectionContext) {
      return recipients ?? [];
    }

    return (
      recipients?.filter(
        (s) => _.intersection(s.collectionContext, collectionContext).length > 0,
      ) ?? []
    );
  }, [collectionContext, recipients]);

  const onChange = ({ appId, included }) => {
    setSelected((prevSelected) => toggleSelection(prevSelected, appId, included));
    Api.dataMap.patchDataMap(orgId, {
      operations: [
        {
          target: "RECIPIENT_ASSOCIATION",
          action: included ? "ADD" : "REMOVE",
          groupId,
          vendorId: appId,
        },
      ],
    });
  };

  const onChangeSelectAll = (selected: boolean) => {
    relevantRecipients.forEach((s) => onChange({ appId: s.vendorId, included: selected }));
  };

  const group = useMemo(() => groups?.find((g) => g.id == groupId), [groups, groupId]);

  // deserialize remote state into local state
  useEffect(() => {
    if (dataMap && group) {
      const associatedVendorIds = dataMap.recipientAssociations
        .filter((d) => d.groupId == group.id)
        .map((d) => d.vendorId);
      setSelected(associatedVendorIds);
    }
  }, [dataMap, group]);

  const error = groupsRequest.error ?? recipientsRequest.error ?? dataMapRequest.error;

  return {
    error,
    selected,
    group,
    fetched: {
      sources: relevantRecipients,
      dataMap,
      collectionGroups: groups,
    },
    requests: {
      sources: recipientsRequest,
      dataMap: dataMapRequest,
      collectionGroups: groupsRequest,
    },
    multiselect: {
      recipients: relevantRecipients,
      selected,
      onChange,
      onChangeSelectAll,
    },
  };
};

export const useDataSourceAssociationForSource = (
  orgId: OrganizationPublicId,
  dataSourceId: UUIDString,
  excludeTypes: CollectionGroupType[] = EXCLUDE_GROUP_EMPLOYMENT,
) => {
  const [groups, groupsRequest] = useCollectionGroups(orgId, undefined, excludeTypes);
  const [sources, sourcesRequest] = useOrgDataSources(orgId);
  const {
    data: dataMap,
    request: dataMapRequest,
    mutators: { patch },
  } = useDataMap(orgId);

  const onChange = ({ groupId, included }) => {
    patch.mutate({
      operations: [
        {
          target: "SOURCE_ASSOCIATION",
          action: included ? "ADD" : "REMOVE",
          groupId: groupId,
          vendorId: dataSourceId,
        },
      ],
    });
  };

  const source = useMemo(
    () => sources?.find((s) => s.vendorId === dataSourceId),
    [sources, dataSourceId],
  );

  const selected = useMemo(
    () =>
      dataMap.sourceAssociations.filter((a) => a.vendorId == dataSourceId).map((a) => a.groupId),
    [dataMap.sourceAssociations, dataSourceId],
  );

  const pending = useDataMapPendingSourceAssocationCollectionGroupIds(dataMap, {
    vendorId: dataSourceId,
  });

  return {
    selected,
    pending,
    source,
    fetched: {
      sources,
      dataMap,
      collectionGroups: groups,
    },
    requests: {
      sources: sourcesRequest,
      dataMap: dataMapRequest,
      collectionGroups: groupsRequest,
    },
    multiselect: {
      collectionGroups: groups,
      selected,
      pending,
      onChange,
    },
  };
};
